import BarChartIcon from '@mui/icons-material/BarChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatShapesIcon from '@mui/icons-material/FormatShapes';
import InfoIcon from '@mui/icons-material/Info';
import InsightsIcon from '@mui/icons-material/Insights';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import { Accordion, AccordionDetails, AccordionSummary, Tooltip } from '@mui/material';
import Badge from '@mui/material/Badge';
import classNames from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { InfoPanel } from '../components/info-panel';
import { LayersToggle } from '../components/layers-toggle';
import { LocationsList } from '../components/locations-list';
import { AccountContext } from '../context/account';
import { ExploreContext } from '../context/explore';
import { LocationsContext } from '../context/locations';
import { MapDataContext } from '../context/map-data';
import { DataSourceModel } from '../core/global';
import { retrieveItem, storeItem } from '../core/storage';
import useLoggedIn from '../hooks/useLoggedIn';
import styles from "../styles/components/navigation-content.module.scss";
import { CustomDataList } from './custom-data-list';
import { ExploreDataSourcesModal } from './explore-data-sources';
import { ModelSwitch } from './models-switch';
import { ProjectsToggle } from './projects-toggle';

const showROI = import.meta.env.VITE_ENABLE_ROI === 'true'
const enableCustomData = import.meta.env.VITE_ENABLE_CUSTOM_DATA === 'true'

interface TitleProps {
  visible?: boolean
  title: string | React.ReactElement<any>
  tooltip?: string | null
  icon?: React.ReactElement<any>
  open?: boolean
  badgeContent?: string | number
  color?: "error" | "success" | "warning" | "info" | "default"
  id?: string
}

export const Title = ({
  visible = false,
  icon = <></>,
  title,
  open = true,
  badgeContent = '',
  color = "success",
  id,
  tooltip =  null,
}: TitleProps) => {
  return (
    <div className={styles.title} id={id}>
      <Badge 
        className={styles.badge} 
        badgeContent={badgeContent} 
        variant={badgeContent === '' ? 'dot' : 'standard'}
        color={color}
        invisible={!visible}
      >
        {open ?
          <>{React.cloneElement(icon, { sx: {color: '#010824'} })}</>
        :
          <Tooltip
            title={tooltip || title} 
            placement="right" 
            arrow
          >{React.cloneElement(icon, { sx: {color: '#010824'} })}</Tooltip>
        }
        
      </Badge>
      <span>{title}</span>
    </div>
  )
}

interface NavigationContentProps {
  open: boolean
  changeOpen: React.Dispatch<React.SetStateAction<boolean>>
  openSourcesModal?: DataSourceModel | null
}

export const NavigationContent = ({
  open,
  changeOpen,
  openSourcesModal = null,
}: NavigationContentProps) => {
  const [expandedPanels, setExpandedPanels] = useState<string[]>([])
  const [disabledPanels, setDisabledPanels] = useState<string[]>([])
  const [showSourcesModal, setShowSourcesModal] = useState(false)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const {
    projects,
    refetchProjects,
    customDataPoints,
  } = useContext(AccountContext);

  const isLoggedIn = useLoggedIn();

  const {
    hasLocations,
    noneVisible,
  } = useContext(LocationsContext);

  const {
    meshDataGeoJson, 
  } = useContext(MapDataContext);

  const {
    exploreLocationParams,
    setExploreLocationParams, 
  } = useContext(ExploreContext);

  useEffect(() => {
    refetchProjects()
  }, [])

  useEffect(() => {
    setShowSourcesModal(openSourcesModal !== null)
  }, [openSourcesModal])

  useEffect(() => {
    const savedLastLocation: any = retrieveItem('map-location') || {}
    const {expandedPanels = [], drawerOpen} = savedLastLocation
    if (!open) {
      setExploreLocationParams(null)
      if (drawerOpen === false) {
        storeExpandedPanelsState([])
      }
      setExpandedPanels([])
      setDisabledPanels([])
    } else {
      if (drawerOpen === true) {
        setExpandedPanels(expandedPanels)
        setDisabledPanels([])
      }
    }
  }, [open])

  const storeExpandedPanelsState = useCallback((panelsState: string[], openDrawer: any = null) => {
    const savedLastLocation: any = retrieveItem('map-location') || {}
    const { drawerOpen } = savedLastLocation
    storeItem('map-location', {
      ...savedLastLocation,
      expandedPanels: panelsState,
      drawerOpen: openDrawer !== null ? openDrawer : drawerOpen,
    })
  }, [])

  const handleChange = useCallback((panel: string) => {
    return (event: React.SyntheticEvent, isExpanded: boolean) => {
      const index = expandedPanels.indexOf(panel)
      if (panel === 'info' && index !== -1 && exploreLocationParams !== null) {
        setExploreLocationParams(null);
      } else {  
        const newPanelState = index === -1 ? 
          [...expandedPanels, panel] 
          : [...expandedPanels].filter(i => i !== panel)

        if (index === -1) {
          changeOpen(true)
          storeExpandedPanelsState(newPanelState, true)
        } else {
          storeExpandedPanelsState(newPanelState)
        }
        setExpandedPanels(newPanelState)
      }
    }
  }, [expandedPanels, exploreLocationParams])

  return (
    <div className={classNames({
      [styles.isOpen]: open
    })}>
      <Accordion
        disabled={disabledPanels.indexOf('model') !== -1}
        className={styles.accordion}
        expanded={disabledPanels.length === 0}
        onChange={handleChange('model')}
      >
        <AccordionSummary
          className={styles.summary}
          // expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
        >
          {open && <div
            className={styles.descriptionButton}
            onClick={()=> {
              if (open) {
                setShowSourcesModal(true)
              }
            }}
          >
            Model description
          </div>}
          {/* <Badge
            badgeContent={"?"} 
            color="info"
            onClick={()=> {
              if (open) {
                setShowSourcesModal(true)
              }
            }}
          >
            <>
              <Title
                open={open}
                title={"Data sources"}  
                icon={<DataThresholdingIcon />}
                visible={false}
              />
            </>
          </Badge> */}
          <Title
              open={open}
              title={"Select model"}  
              icon={<TravelExploreIcon />}
              visible={false}
            />
        </AccordionSummary>
        <AccordionDetails className={styles.details} id='data-sources-accordion'>
          <ModelSwitch />
        </AccordionDetails>
      </Accordion>

      <Accordion
        id="selected-location-accordion"
        className={styles.accordion}
        expanded={disabledPanels.length > 0 || expandedPanels.indexOf('info') !== -1}
        onChange={handleChange('info')}
      >
        <AccordionSummary
          className={styles.summary}
          expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
        >
          <Title
            open={open}
            title={"Selected location information"}
            icon={<InfoIcon />}
            visible={!!(meshDataGeoJson)}
          />
        </AccordionSummary>
        <AccordionDetails className={styles.details}>
          <InfoPanel onToggleExplore={(panels: string[]) => {
            setDisabledPanels(panels)
          }} />
        </AccordionDetails>
      </Accordion>

      {isLoggedIn && 
        <Accordion
          disabled={disabledPanels.indexOf('data') !== -1}
          className={styles.accordion}
          expanded={disabledPanels.length === 0 && expandedPanels.indexOf('data') !== -1}
          onChange={handleChange('data')}
        >
          <AccordionSummary
            className={styles.summary}
            expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
          >
            <Title
              open={open}
              title={"Purchased data"}
              icon={<BarChartIcon />}
              visible={projects.length > 0}
            />
          </AccordionSummary>
          {!(disabledPanels.indexOf('data') !== -1) && 
            <AccordionDetails className={classNames(styles.details, styles.fullWidth)}>
              {projects.length === 0 ?
                <>
                  <p>Your purchased data will show in this area.</p>
                </>
                :
                <ProjectsToggle />
              }
            </AccordionDetails>
          }
        </Accordion>
      }

      {isLoggedIn && enableCustomData &&
        <Accordion
          disabled={disabledPanels.indexOf('custom') !== -1}
          className={styles.accordion}
          expanded={disabledPanels.length === 0 && expandedPanels.indexOf('custom') !== -1}
          onChange={handleChange('custom')}
        >
          <AccordionSummary
            className={styles.summary}
            expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
          >
            <Title
              open={open}
              title={"Custom data"}
              icon={<InsightsIcon />}
              // visible={customDataPoints.length > 0}
            />
          </AccordionSummary>
          {!(disabledPanels.indexOf('custom') !== -1) && 
            <AccordionDetails className={classNames(styles.details, styles.fullWidth)}>
              <CustomDataList />
            </AccordionDetails>
          }
        </Accordion>
      }

      {isLoggedIn && showROI && <Accordion
        disabled={disabledPanels.indexOf('regions') !== -1}
        className={styles.accordion}
        expanded={disabledPanels.length === 0 && expandedPanels.indexOf('regions') !== -1}
        onChange={handleChange('regions')}
      >
        <AccordionSummary
          className={styles.summary}
          expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
        >
          <Title
            open={open}
            title={"Regions of interest"}
            icon={<FormatShapesIcon />}
            visible={!!(hasLocations && !noneVisible)}
          />
        </AccordionSummary>
        <AccordionDetails sx={{pb: 0}} className={classNames(styles.details)}>
          <LocationsList />
        </AccordionDetails>
      </Accordion>}

      <Accordion
        disabled={disabledPanels.indexOf('layers') !== -1}
        className={styles.accordion}
        expanded={disabledPanels.length === 0 && expandedPanels.indexOf('layers') !== -1}
        onChange={handleChange('layers')}
      >
        <AccordionSummary
          className={styles.summary}
          expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
        >
          <Title
            open={open}
            title={"Layers"}
            icon={<LayersIcon />}
            visible={false}
          />
        </AccordionSummary>
        <AccordionDetails className={classNames(styles.details)}>
          <LayersToggle type="layers" />
        </AccordionDetails>
      </Accordion>

      <Accordion
        disabled={disabledPanels.indexOf('maps') !== -1}
        className={styles.accordion}
        expanded={disabledPanels.length === 0 && expandedPanels.indexOf('maps') !== -1}
        onChange={handleChange('maps')}
      >
        <AccordionSummary
          className={styles.summary}
          expandIcon={<ExpandMoreIcon sx={{color: '#010824'}} />}
        >
          <Title
            open={open}
            title={"Background maps"}
            icon={<MapIcon />}
            visible={false}
          />
        </AccordionSummary>
        <AccordionDetails className={classNames(styles.details)}>
          <LayersToggle type="providers" />
        </AccordionDetails>
      </Accordion>
      
      {showSourcesModal && 
      <ExploreDataSourcesModal
        selectedModel={openSourcesModal}
        open
        onClose={() => {
          setShowSourcesModal(false)
          if (pathname && typeof pathname === 'string' && pathname.includes('/models/')) {
            navigate('/dashboard')
          }
        }}
      />}
    </div>
  )
}